import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/with-selector';

interface TabScopedRegistryProviderProps {
  children: React.ReactNode;
  tabId: UUID;
}

interface OwnedTabScopedRegistryOptions<TState> {
  initialState: TState;
  useTabIdErrorMessage: string;
  areEqual?: (left: TState, right: TState) => boolean;
  onPurge?: (tabId: UUID) => void;
}

interface PublishedTabScopedRegistryProviderProps<TState> {
  state: TState;
  tabId: UUID;
  children?: React.ReactNode;
}

interface PublishedTabScopedRegistryOptions {
  useTabIdErrorMessage: string;
}

const purgeClosedTabsCallbacks: ((liveTabIds: Set<UUID>) => void)[] = [];

/**
 * Frees the state every owned registry holds for tabs that are no longer open.
 *
 * A provider cannot free its own tab's state on effect cleanup: tab panes stay mounted
 * under `React.Activity`, which runs cleanup every time a pane is hidden. Deleting there
 * resets a tab's state on every tab switch, and values that are only written by an event
 * handler (react-window's scroll offset, for one) come back as their initial value while
 * the DOM keeps the real one. Liveness is instead decided by the tab area, which knows
 * which tabs it still renders.
 */
export function purgeClosedTabScopedState(liveTabIds: UUID[]): void {
  const liveTabIdSet = new Set(liveTabIds);
  for (const purgeClosedTabs of purgeClosedTabsCallbacks) purgeClosedTabs(liveTabIdSet);
}

export function createOwnedTabScopedRegistry<TState>({
  areEqual,
  initialState,
  onPurge,
  useTabIdErrorMessage,
}: OwnedTabScopedRegistryOptions<TState>) {
  const stateByTabId = new Map<UUID, TState>();
  const listenersByTabId = new Map<UUID, Set<() => void>>();
  const TabScopedRegistryContext = createContext<UUID | null>(null);

  purgeClosedTabsCallbacks.push(purgeClosedTabs);

  function Provider({ children, tabId }: TabScopedRegistryProviderProps) {
    const contextValue = useMemo(() => tabId, [tabId]);

    useEffect(() => {
      ensure(tabId);
    }, [tabId]);

    return (
      <TabScopedRegistryContext.Provider value={contextValue}>
        {children}
      </TabScopedRegistryContext.Provider>
    );
  }

  function Scope({ children, tabId }: TabScopedRegistryProviderProps) {
    const contextValue = useMemo(() => tabId, [tabId]);

    return (
      <TabScopedRegistryContext.Provider value={contextValue}>
        {children}
      </TabScopedRegistryContext.Provider>
    );
  }

  function useSelector<TValue>(
    selector: (state: TState) => TValue,
    isEqual?: (left: TValue, right: TValue) => boolean
  ): TValue {
    const tabId = useMaybeTabId();
    return useSelectorForTabId(tabId, selector, isEqual);
  }

  function useSelectorForTabId<TValue>(
    tabId: UUID | null,
    selector: (state: TState) => TValue,
    isEqual?: (left: TValue, right: TValue) => boolean
  ): TValue {
    return useSyncExternalStoreWithSelector(
      listener => subscribe(tabId, listener),
      () => getSnapshot(tabId),
      () => initialState,
      selector,
      isEqual
    );
  }

  function useMaybeTabId() {
    return useContext(TabScopedRegistryContext);
  }

  function useTabId() {
    const tabId = useMaybeTabId();
    if (!tabId) throw new Error(useTabIdErrorMessage);
    return tabId;
  }

  function getForTabId(tabId: UUID | null) {
    if (!tabId) return null;
    return stateByTabId.get(tabId) ?? null;
  }

  function updateForTabId(tabId: UUID, updater: (state: TState) => TState) {
    const previousState = ensure(tabId);
    const nextState = updater(previousState);
    setForTabId(tabId, nextState);
  }

  function getSnapshot(tabId: UUID | null) {
    return getForTabId(tabId) ?? initialState;
  }

  function purgeClosedTabs(liveTabIds: Set<UUID>) {
    for (const tabId of stateByTabId.keys()) {
      if (liveTabIds.has(tabId)) continue;
      stateByTabId.delete(tabId);
      onPurge?.(tabId);
      emitChange(tabId);
    }
  }

  function ensure(tabId: UUID) {
    const existingState = stateByTabId.get(tabId);
    if (existingState) return existingState;

    stateByTabId.set(tabId, initialState);
    emitChange(tabId);
    return initialState;
  }

  function setForTabId(tabId: UUID, state: TState) {
    const previousState = stateByTabId.get(tabId);
    if (previousState === state) return;
    if (previousState && areEqual?.(previousState, state)) return;

    stateByTabId.set(tabId, state);
    emitChange(tabId);
  }

  function subscribe(tabId: UUID | null, listener: () => void) {
    if (!tabId) return () => {};

    const listeners = getListenersForTabId(tabId);
    listeners.add(listener);

    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) listenersByTabId.delete(tabId);
    };
  }

  function getListenersForTabId(tabId: UUID) {
    let listeners = listenersByTabId.get(tabId);
    if (!listeners) {
      listeners = new Set();
      listenersByTabId.set(tabId, listeners);
    }
    return listeners;
  }

  function emitChange(tabId: UUID) {
    listenersByTabId.get(tabId)?.forEach(listener => listener());
  }

  return {
    Provider,
    Scope,
    getForTabId,
    updateForTabId,
    useMaybeTabId,
    useSelector,
    useSelectorForTabId,
    useTabId,
  };
}

export function createPublishedTabScopedRegistry<TState>({
  useTabIdErrorMessage,
}: PublishedTabScopedRegistryOptions) {
  const stateByTabId = new Map<UUID, TState>();
  const listenersByTabId = new Map<UUID, Set<() => void>>();
  const StateContext = createContext<TState | null>(null);
  const TabScopedRegistryContext = createContext<UUID | null>(null);

  function Provider({ children, state, tabId }: PublishedTabScopedRegistryProviderProps<TState>) {
    const contextTabId = useMemo(() => tabId, [tabId]);

    useEffect(() => {
      publish(tabId, state);
      return () => unpublish(tabId, state);
    }, [state, tabId]);

    return (
      <TabScopedRegistryContext.Provider value={contextTabId}>
        <StateContext.Provider value={state}>{children}</StateContext.Provider>
      </TabScopedRegistryContext.Provider>
    );
  }

  function Scope({ children, tabId }: TabScopedRegistryProviderProps) {
    const state = useStateForTabId(tabId);
    const contextTabId = useMemo(() => tabId, [tabId]);

    return (
      <TabScopedRegistryContext.Provider value={contextTabId}>
        <StateContext.Provider value={state}>{children}</StateContext.Provider>
      </TabScopedRegistryContext.Provider>
    );
  }

  function useMaybeState() {
    return useContext(StateContext);
  }

  function useSelector<TValue>(
    selector: (state: TState | null) => TValue,
    isEqual?: (left: TValue, right: TValue) => boolean
  ): TValue {
    const tabId = useMaybeTabId();
    return useSelectorForTabId(tabId, selector, isEqual);
  }

  function useSelectorForTabId<TValue>(
    tabId: UUID | null,
    selector: (state: TState | null) => TValue,
    isEqual?: (left: TValue, right: TValue) => boolean
  ): TValue {
    return useSyncExternalStoreWithSelector(
      listener => subscribe(tabId, listener),
      () => getForTabId(tabId),
      () => null,
      selector,
      isEqual
    );
  }

  function useMaybeTabId() {
    return useContext(TabScopedRegistryContext);
  }

  function useTabId() {
    const tabId = useMaybeTabId();
    if (!tabId) throw new Error(useTabIdErrorMessage);
    return tabId;
  }

  function getForTabId(tabId: UUID | null) {
    if (!tabId) return null;
    return stateByTabId.get(tabId) ?? null;
  }

  function publish(tabId: UUID, state: TState) {
    const previousState = stateByTabId.get(tabId);
    if (previousState === state) return;

    stateByTabId.set(tabId, state);
    emitChange(tabId);
  }

  function unpublish(tabId: UUID, state: TState) {
    if (stateByTabId.get(tabId) !== state) return;
    stateByTabId.delete(tabId);
    emitChange(tabId);
  }

  function useStateForTabId(tabId: UUID | null) {
    return useSelectorForTabId(tabId, state => state);
  }

  function subscribe(tabId: UUID | null, listener: () => void) {
    if (!tabId) return () => {};

    const listeners = getListenersForTabId(tabId);
    listeners.add(listener);

    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) listenersByTabId.delete(tabId);
    };
  }

  function getListenersForTabId(tabId: UUID) {
    let listeners = listenersByTabId.get(tabId);
    if (!listeners) {
      listeners = new Set();
      listenersByTabId.set(tabId, listeners);
    }
    return listeners;
  }

  function emitChange(tabId: UUID) {
    listenersByTabId.get(tabId)?.forEach(listener => listener());
  }

  return {
    Provider,
    Scope,
    getForTabId,
    useMaybeState,
    useMaybeTabId,
    useSelector,
    useSelectorForTabId,
    useTabId,
  };
}
